export const privacyPolicyContent = {
  title: 'Privacy Policy',
  eyebrow: 'Legal',
  lastUpdated: 'June 17, 2026',
  intro:
    'At ReportWerks, we value your privacy and work to protect the information you share with us. This Privacy Policy explains how we collect, use, and safeguard information when you use our website, services, connected integrations, Google API connections, and AI-assisted reporting workflows.',
  sections: [
    {
      heading: 'Information We Collect',
      blocks: [
        {
          title: '1. Information You Provide',
          bullets: [
            'Account registration details such as your name, email address, and organization name.',
            'Information entered into forms or dashboards within ReportWerks.',
            'Configuration details needed to connect reporting sources, define views, and control access inside your workspace.',
          ],
        },
        {
          title: '2. Automatically Collected Information',
          paragraphs: [
            'When you visit our website or use our services, we may automatically collect device and browser type, IP address, referring and exit pages, date and time of visits, and standard analytics information from services such as Google Analytics or similar tools.',
            'This data helps us understand usage patterns, improve functionality, and optimize the experience.',
          ],
        },
        {
          title: '3. OAuth and Connected Accounts',
          paragraphs: [
            'When you connect third-party platforms such as Google Ads, Meta Ads, LinkedIn, Constant Contact, or Microsoft Advertising through OAuth authorization, ReportWerks requests limited access tokens from those providers.',
          ],
          bullets: [
            'We do not collect or store your third-party usernames or passwords.',
            'Access tokens allow ReportWerks to securely fetch reporting and analytics data on your behalf.',
            'Tokens may be refreshed automatically to maintain service continuity.',
            'You may revoke access at any time through the third-party provider or within ReportWerks.',
          ],
        },
        {
          title: '4. AI-Assisted Analysis Data',
          paragraphs: [
            'ReportWerks may use AI-assisted workflows to help summarize performance, surface patterns faster, and speed up analysis across reporting views.',
          ],
          bullets: [
            'These workflows are designed around campaign, channel, and performance statistics rather than personally identifiable information.',
            'We do not intentionally pass PII such as contact names, personal email addresses, phone numbers, donor records, customer records, or third-party login credentials into AI analysis workflows.',
            'Where AI-assisted analysis is used, the goal is to help teams interpret reporting data faster, not to profile individuals.',
          ],
        },
      ],
    },
    {
      heading: 'Google API Data Use',
      blocks: [
        {
          paragraphs: [
            'If you authorize ReportWerks to connect to a Google service, ReportWerks accesses only the Google user data needed to provide the reporting features you requested.',
          ],
          bullets: [
            'Google data accessed may include Google account identifiers, Google Ads customer and account identifiers, campaign names, ad group names, performance metrics, spend, clicks, impressions, conversions, conversion values, dates, labels, and other reporting fields made available by the Google APIs and authorized by you.',
            'ReportWerks uses Google user data to import reporting data, show connection status, build dashboards, prepare reporting summaries, support AI-assisted reporting analysis, and create client-ready report views.',
            'ReportWerks does not use Google user data for targeted advertising, personalized advertising, retargeting, credit decisions, lending decisions, sale to data brokers, or sale to information resellers.',
            'ReportWerks does not use Google user data to train generalized AI or machine learning models.',
            'ReportWerks does not modify your Google campaigns or Google accounts through reporting connections unless a feature explicitly asks you to authorize that action.',
          ],
        },
      ],
    },
    {
      heading: 'How We Use Your Information',
      blocks: [
        {
          bullets: [
            'Provide, maintain, and improve our reporting services.',
            'Display integrated marketing and fundraising data in your dashboards.',
            'Use campaign and performance statistics to generate faster summaries, pattern detection, and analysis support inside ReportWerks.',
            'Authenticate your account and authorize access to connected services.',
            'Communicate with you about updates, features, or support inquiries.',
            'Monitor system performance, detect abuse, and maintain security.',
          ],
        },
      ],
    },
    {
      heading: 'Data Sharing and Disclosure',
      blocks: [
        {
          bullets: [
            'We may share limited data with service providers that support analytics, hosting, security, or customer support.',
            'Where AI-enabled service providers are used to support analysis features, the data shared is limited to the statistics and campaign-level reporting data needed to provide the user-facing reporting features you requested.',
            'Google user data is not transferred to third parties except as needed to provide or improve ReportWerks user-facing features with your consent, maintain security, comply with law, or complete a merger, acquisition, or sale of assets after required user notice or consent.',
            'We may disclose information if required by law, subpoena, or legal process.',
            'ReportWerks does not sell, rent, or trade your personal data or connected account information.',
          ],
        },
      ],
    },
    {
      heading: 'Data Security',
      blocks: [
        {
          paragraphs: [
            'We use industry-standard security practices to protect your information, including encryption in transit and secure token storage. No system is completely secure, and users should manage OAuth permissions carefully.',
            'We also design AI-assisted workflows to minimize sensitive data exposure by focusing those workflows on reporting metrics, campaign data, and aggregate performance statistics instead of PII.',
            'Google user data is protected using reasonable administrative, technical, and organizational safeguards designed to prevent unauthorized access, disclosure, alteration, or destruction.',
          ],
        },
      ],
    },
    {
      heading: 'Data Retention',
      blocks: [
        {
          bullets: [
            'Website analytics data may be retained in aggregate form for trend analysis.',
            'OAuth tokens and associated reporting data are retained only while your account remains active or until you revoke access.',
            'When you disconnect a Google integration, ReportWerks stops using the revoked connection to retrieve new Google user data.',
            'You may request deletion of your account, connected account data, and associated reporting data at any time by contacting support@reportwerks.com.',
          ],
        },
      ],
    },
    {
      heading: 'Your Choices',
      blocks: [
        {
          bullets: [
            'You may opt out of non-essential cookies through your browser settings.',
            'You can disconnect third-party integrations from your ReportWerks account or from the provider security dashboard.',
            'You can revoke Google access from your Google Account permissions page at any time.',
            'You may unsubscribe from marketing emails using the link in the message.',
          ],
        },
      ],
    },
    {
      heading: 'Google API Services User Data Policy',
      blocks: [
        {
          paragraphs: [
            'ReportWerks use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.',
          ],
        },
      ],
    },
    {
      heading: "Children's Privacy",
      blocks: [
        {
          paragraphs: [
            'ReportWerks is not directed toward individuals under the age of 16, and we do not knowingly collect data from children.',
          ],
        },
      ],
    },
    {
      heading: 'Changes to This Policy',
      blocks: [
        {
          paragraphs: [
            'We may update this Privacy Policy from time to time. Significant changes may be communicated by email or through a prominent notice on the website.',
          ],
        },
      ],
    },
    {
      heading: 'Contact Us',
      blocks: [
        {
          paragraphs: [
            'If you have questions about this Privacy Policy or how your information is handled, please contact ReportWerks at support@reportwerks.com.',
          ],
        },
      ],
    },
  ],
};

export const termsOfServiceContent = {
  title: 'Terms of Service',
  eyebrow: 'Legal',
  lastUpdated: 'June 17, 2026',
  intro:
    'Welcome to ReportWerks. By using our website, services, integrations, and AI-assisted analysis features, you agree to the following Terms of Service. Please read them carefully. If you do not agree, you may not use our services.',
  sections: [
    {
      heading: '1. Eligibility',
      blocks: [
        {
          paragraphs: [
            'You must be at least 18 years old and legally capable of entering into contracts to use ReportWerks.',
          ],
        },
      ],
    },
    {
      heading: '2. Account Responsibilities',
      blocks: [
        {
          bullets: [
            'You are responsible for safeguarding your login credentials.',
            'You agree not to share account access with unauthorized individuals.',
            'You are responsible for all activity that occurs under your account.',
          ],
        },
      ],
    },
    {
      heading: '3. Use of Services',
      blocks: [
        {
          bullets: [
            'ReportWerks provides analytics, reporting dashboards, and AI-assisted analysis tools for nonprofits, agencies, and related organizations.',
            'You agree to use the service only for lawful purposes and in accordance with applicable laws and regulations.',
            'You may not attempt to disrupt, overload, or reverse engineer our systems.',
          ],
        },
      ],
    },
    {
      heading: '4. OAuth and Connected Accounts',
      blocks: [
        {
          paragraphs: [
            'When you connect third-party platforms such as Google Ads, Meta, LinkedIn, Constant Contact, or Microsoft Advertising:',
          ],
          bullets: [
            'You authorize ReportWerks to use OAuth to obtain access tokens.',
            'Tokens are used exclusively to fetch and display reporting data in your dashboards.',
            'You may revoke access at any time through the provider security settings or within ReportWerks.',
            'You understand that revoking access may limit functionality.',
          ],
        },
      ],
    },
    {
      heading: '5. Data and Content',
      blocks: [
        {
          bullets: [
            'You retain ownership of your data.',
            'By using ReportWerks, you grant us a limited license to process and display your data as necessary to provide the service.',
            'We may store aggregated, anonymized usage statistics for system improvement.',
          ],
        },
      ],
    },
    {
      heading: '6. AI-Assisted Analysis',
      blocks: [
        {
          paragraphs: [
            'ReportWerks may use AI-assisted workflows to help organize reporting, speed up data interpretation, and generate analysis faster from campaign and performance data.',
          ],
          bullets: [
            'These workflows are intended to work on reporting metrics, aggregate statistics, and campaign-level data.',
            'They are not intended to process personally identifiable information, donor records, customer records, or third-party credentials.',
            'You agree not to intentionally submit sensitive personal data into areas of the service designed for AI-assisted reporting analysis unless explicitly supported by the platform.',
          ],
        },
      ],
    },
    {
      heading: '7. Service Availability',
      blocks: [
        {
          bullets: [
            'We strive for high availability but do not guarantee uninterrupted or error-free service.',
            'Planned maintenance or unforeseen outages may temporarily limit access.',
          ],
        },
      ],
    },
    {
      heading: '8. Fees and Payment',
      blocks: [
        {
          bullets: [
            'Some features may require a paid subscription.',
            'Fees, payment terms, and renewal conditions will be provided at the time of subscription.',
            'Failure to pay may result in suspension or termination of your account.',
          ],
        },
      ],
    },
    {
      heading: '9. Disclaimer of Warranties',
      blocks: [
        {
          paragraphs: [
            'ReportWerks is provided "as is" and "as available" without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.',
          ],
        },
      ],
    },
    {
      heading: '10. Limitation of Liability',
      blocks: [
        {
          paragraphs: ['To the maximum extent permitted by law:'],
          bullets: [
            'ReportWerks and its affiliates will not be liable for indirect, incidental, special, consequential, or punitive damages.',
            'Our total liability for any claim arising from the services will not exceed the fees paid in the 12 months preceding the claim.',
          ],
        },
      ],
    },
    {
      heading: '11. Termination',
      blocks: [
        {
          paragraphs: [
            'We may suspend or terminate your account if you violate these Terms. Upon termination, you lose access to the service and associated data, subject to applicable retention and deletion policies.',
          ],
        },
      ],
    },
    {
      heading: '12. Governing Law',
      blocks: [
        {
          paragraphs: [
            'These Terms are governed by the laws of the State of Georgia, USA, without regard to conflict of law principles. Disputes will be resolved in the courts located in Cherokee County, Georgia.',
          ],
        },
      ],
    },
    {
      heading: '13. Changes to Terms',
      blocks: [
        {
          paragraphs: [
            'We may update these Terms periodically. Continued use of ReportWerks after changes constitutes acceptance of the updated Terms.',
          ],
        },
      ],
    },
    {
      heading: '14. Contact',
      blocks: [
        {
          paragraphs: [
            'If you have questions about these Terms, please contact ReportWerks at support@reportwerks.com.',
          ],
        },
      ],
    },
  ],
};
